// src/pages/HRDigitalHub/components/Email.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HRLayout from './HRLayout';
import api from '../../api/client';
import {
  ArrowLeft, Mail, Send, FileText, Trash2, Plus, Search, X, Paperclip,
  Download, CheckCircle, AlertCircle
} from 'lucide-react';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  html: string;
}

interface EmailHistory {
  id: string;
  from: string;
  to: string;
  subject: string;
  html: string;
  status: string;
  sentAt: string;
  sentBy: { name: string };
  attachments?: Array<{
    filename: string;
    contentType: string;
    size: number;
  }>;
}

const decodeMimeHeader = (value: string): string => {
  try {
    if (!value) return value;
    const regex = /=\?([^?]+)\?([BQ])\?([^?]*)\?=/gi;
    let result = value.replace(regex, (match: string, charset: string, encoding: string, encodedText: string): string => {
      try {
        if (encoding.toUpperCase() === 'B') {
          const decodedBytes = atob(encodedText);
          return new TextDecoder(charset || 'utf-8').decode(
            Uint8Array.from(decodedBytes, (c) => c.charCodeAt(0))
          );
        } else if (encoding.toUpperCase() === 'Q') {
          return encodedText
            .replace(/_/g, ' ')
            .replace(/=([0-9A-Fa-f]{2})/g, (_sub: string, hex: string) =>
              String.fromCharCode(parseInt(hex, 16))
            );
        }
      } catch (e) {
        // fallback
      }
      return encodedText;
    });
    return result.replace(/\s+/g, ' ').trim();
  } catch {
    return value;
  }
};

const fixEncoding = (str: string): string => {
  try {
    if (/[\x80-\xFF]{3,}/.test(str)) {
      const bytes = Uint8Array.from(str, c => c.charCodeAt(0));
      return new TextDecoder('utf-8').decode(bytes);
    }
  } catch (e) {
    // fallback
  }
  return str;
};

const getReadableFilename = (name: string): string => {
  const decoded = decodeMimeHeader(name);
  return fixEncoding(decoded);
};

const Email: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const emailTo = searchParams.get('to') || '';
  const candidateId = searchParams.get('candidateId');
  const confirmDialog = useConfirm();

  const [activeTab, setActiveTab] = useState<'compose' | 'history' | 'templates'>('compose');
  const [to, setTo] = useState(emailTo);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', html: '' });
  const [showFullTemplate, setShowFullTemplate] = useState<string | null>(null);

  const [historyItems, setHistoryItems] = useState<EmailHistory[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedEmailIds, setSelectedEmailIds] = useState<string[]>([]);
  const [viewedEmail, setViewedEmail] = useState<EmailHistory | null>(null);

  const observerRef = useRef<HTMLDivElement | null>(null);
  const PAGE_SIZE = 20;

  const loadHistoryPage = async (page: number, reset: boolean = false) => {
    setLoadingHistory(true);
    try {
      const params: any = { page, limit: PAGE_SIZE };
      if (historySearch.trim()) params.search = historySearch.trim();
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;

      const res = await api.get('/email/history', { params });
      const newEmails = (res.data.items || []).map((email: any) => ({
        ...email,
        id: email._id || email.id,
        attachments: (email.attachments || []).map((att: any) => ({
          ...att,
          filename: getReadableFilename(att.filename || ''),
        })),
      }));

      if (reset) {
        setHistoryItems(newEmails);
        setSelectedEmailIds([]);
      } else {
        setHistoryItems(prev => [...prev, ...newEmails]);
      }
      setHasMoreHistory(newEmails.length === PAGE_SIZE);
    } catch (err) {
      console.error('Ошибка загрузки истории:', err);
      showToast('Не удалось загрузить историю', 'error');
      if (reset) setHistoryItems([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const resetAndLoadHistory = () => {
    setHistoryPage(1);
    loadHistoryPage(1, true);
  };

  const loadNextPage = useCallback(() => {
    if (loadingHistory || !hasMoreHistory) return;
    const nextPage = historyPage + 1;
    setHistoryPage(nextPage);
    loadHistoryPage(nextPage, false);
  }, [loadingHistory, hasMoreHistory, historyPage, historySearch, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (activeTab === 'history') resetAndLoadHistory();
  }, [activeTab, historySearch, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    if (!observerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreHistory && !loadingHistory) {
          loadNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [activeTab, hasMoreHistory, loadingHistory, loadNextPage]);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/email/templates?type=general');
      setTemplates(
        res.data.map((t: any) => ({
          id: t._id,
          name: t.name,
          subject: t.subject,
          html: t.html,
        }))
      );
    } catch (err) {
      showToast('Не удалось загрузить шаблоны', 'error');
    }
  };

  useEffect(() => {
    if (activeTab === 'templates') loadTemplates();
  }, [activeTab]);

  const handleSend = async () => {
    if (!to || !subject || !html) {
      showToast('Заполните все поля', 'error');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('to', to);
      formData.append('subject', subject);
      formData.append('html', html);
      attachments.forEach(file => formData.append('attachments', file));
      await api.post('/email/send', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast('Письмо успешно отправлено', 'success');
      setTo('');
      setSubject('');
      setHtml('');
      setAttachments([]);
      if (activeTab === 'history') resetAndLoadHistory();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Ошибка отправки', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!newTemplate.name || !newTemplate.subject || !newTemplate.html) {
      showToast('Заполните все поля шаблона', 'error');
      return;
    }
    try {
      await api.post('/email/templates', { ...newTemplate, type: 'general' });
      showToast('Шаблон сохранён', 'success');
      setShowTemplateForm(false);
      setNewTemplate({ name: '', subject: '', html: '' });
      loadTemplates();
    } catch (err) {
      showToast('Ошибка сохранения шаблона', 'error');
    }
  };

  const handleDeleteTemplate = (id: string, name: string) => {
    confirmDialog.showConfirm({
      title: 'Удаление шаблона',
      message: `Удалить шаблон "${name}"?`,
      onConfirm: async () => {
        try {
          await api.delete(`/email/templates/${id}`);
          showToast('Шаблон удалён', 'success');
          loadTemplates();
        } catch (err) {
          showToast('Ошибка удаления шаблона', 'error');
        }
      },
    });
  };

  const applyTemplate = (template: EmailTemplate) => {
    setSubject(template.subject);
    setHtml(template.html);
    setActiveTab('compose');
  };

  const handleDeleteSingleEmail = (emailId: string) => {
    confirmDialog.showConfirm({
      title: 'Удаление письма',
      message: 'Удалить это письмо из истории?',
      onConfirm: async () => {
        try {
          await api.delete(`/email/history/${emailId}`);
          showToast('Письмо удалено', 'success');
          resetAndLoadHistory();
          if (viewedEmail?.id === emailId) setViewedEmail(null);
        } catch (err: any) {
          showToast(err.response?.data?.error || 'Не удалось удалить', 'error');
        }
      },
    });
  };

  const handleMassDelete = () => {
    if (selectedEmailIds.length === 0) {
      showToast('Не выбрано ни одного письма', 'error');
      return;
    }
    confirmDialog.showConfirm({
      title: 'Массовое удаление',
      message: `Удалить ${selectedEmailIds.length} писем?`,
      onConfirm: async () => {
        try {
          await Promise.all(selectedEmailIds.map(id => api.delete(`/email/history/${id}`)));
          showToast(`Удалено ${selectedEmailIds.length} писем`, 'success');
          resetAndLoadHistory();
        } catch (err) {
          showToast('Не удалось удалить некоторые письма', 'error');
          resetAndLoadHistory();
        }
      },
    });
  };

  const allSelected =
    historyItems.length > 0 &&
    historyItems.every(email => selectedEmailIds.includes(email.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedEmailIds([]);
    } else {
      setSelectedEmailIds(historyItems.map(email => email.id));
    }
  };

  const handleSelectOne = (emailId: string, checked: boolean) => {
    if (checked) {
      setSelectedEmailIds(prev => [...prev, emailId]);
    } else {
      setSelectedEmailIds(prev => prev.filter(id => id !== emailId));
    }
  };

  const clearDateFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const downloadAttachment = async (emailId: string, index: number) => {
    try {
      const response = await api.get(`/email/attachments/${emailId}/${index}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось скачать', 'error');
    }
  };

  const handleBack = () => {
    if (candidateId) {
      navigate(`/app/hr/candidates/${candidateId}`);
    } else {
      navigate('/app/hr/candidates');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <HRLayout>
      <style>{`
        .email-tab-btn {
          transition: all 0.25s ease;
        }
        .email-tab-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }
        .email-tab-btn.active {
          background: linear-gradient(135deg, #6a11cb, #2575fc);
          color: white;
          border: none;
          box-shadow: 0 8px 20px rgba(37,117,252,0.3);
        }
        .email-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .email-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px rgba(0,0,0,0.1);
        }
        .send-btn {
          transition: all 0.2s ease;
        }
        .send-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(37,117,252,0.4);
        }
        .action-btn {
          transition: all 0.2s ease;
        }
        .action-btn:hover {
          background: rgba(37,117,252,0.1) !important;
          transform: scale(1.05);
        }
        .template-item {
          transition: all 0.2s ease;
        }
        .template-item:hover {
          background: rgba(37,117,252,0.03) !important;
          transform: translateX(4px);
        }
        .history-row {
          transition: all 0.15s ease;
          cursor: pointer;
        }
        .history-row:hover {
          background: rgba(37,117,252,0.04) !important;
        }
        .trash-btn {
          transition: all 0.2s ease;
          opacity: 0.6;
        }
        .trash-btn:hover {
          opacity: 1;
          background: rgba(255,77,79,0.1) !important;
          transform: scale(1.1);
        }
      `}</style>

      <div style={styles.container}>
        <button onClick={handleBack} style={styles.backButton}>
          <ArrowLeft size={18} style={{ marginRight: 6 }} />
          {candidateId ? 'Назад к кандидату' : 'К списку кандидатов'}
        </button>

        {/* Табы */}
        <div style={styles.tabs}>
          {[
            { key: 'compose', icon: <Mail size={16} />, label: 'Написать' },
            { key: 'history', icon: <FileText size={16} />, label: 'История' },
            { key: 'templates', icon: <FileText size={16} />, label: 'Шаблоны' },
          ].map(tab => (
            <button
              key={tab.key}
              className={`email-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key as any)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key ? styles.activeTab : {}),
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ========== Вкладка НАПИСАТЬ ========== */}
        {activeTab === 'compose' && (
          <div className="email-card" style={styles.card}>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>
                <Mail size={14} style={{ marginRight: 6 }} />
                Кому *
              </label>
              <input
                type="email"
                placeholder="email@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={styles.input}
                className="form-input"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>
                <FileText size={14} style={{ marginRight: 6 }} />
                Тема *
              </label>
              <input
                type="text"
                placeholder="Тема письма"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={styles.input}
                className="form-input"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Текст письма (HTML) *</label>
              <textarea
                placeholder="Введите текст письма..."
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                rows={10}
                style={styles.textarea}
                className="form-input"
              />
            </div>
            <div style={styles.attachmentSection}>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={styles.attachButton}
              >
                <Paperclip size={16} /> Прикрепить файлы
              </button>
              {attachments.length > 0 && (
                <div style={styles.attachmentsList}>
                  {attachments.map((file, idx) => (
                    <div key={idx} style={styles.attachmentItem}>
                      <span>{file.name}</span>
                      <button
                        onClick={() => removeAttachment(idx)}
                        style={styles.removeAttachment}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.buttonRow}>
              <select
                onChange={(e) => {
                  const t = templates.find(t => t.id === e.target.value);
                  if (t) applyTemplate(t);
                }}
                style={styles.select}
                value=""
              >
                <option value="">Выбрать шаблон</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleSend}
                disabled={loading}
                className="send-btn"
                style={styles.sendButton}
              >
                <Send size={16} /> {loading ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        )}

        {/* ========== Вкладка ИСТОРИЯ ========== */}
        {activeTab === 'history' && (
          <div className="email-card" style={styles.card}>
            <div style={styles.filterBar}>
              <div style={styles.searchWrapper}>
                <Search size={18} style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Поиск по получателю, теме..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              <div style={styles.dateFilters}>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  style={styles.dateInput}
                />
                <span>—</span>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  style={styles.dateInput}
                />
                <button onClick={clearDateFilters} style={styles.clearDatesBtn}>
                  <X size={14} /> Сброс
                </button>
              </div>
            </div>
            {historyItems.length > 0 && (
              <div style={styles.bulkActions}>
                <button
                  onClick={handleSelectAll}
                  className="action-btn"
                  style={styles.bulkButton}
                >
                  {allSelected ? 'Снять все' : 'Выбрать все'}
                </button>
                {selectedEmailIds.length > 0 && (
                  <button
                    onClick={handleMassDelete}
                    className="action-btn"
                    style={{
                      ...styles.bulkButton,
                      background: 'rgba(255,77,79,0.1)',
                      borderColor: '#ff4d4f',
                      color: '#ff4d4f',
                    }}
                  >
                    <Trash2 size={14} /> Удалить ({selectedEmailIds.length})
                  </button>
                )}
              </div>
            )}
            {historyItems.length === 0 && !loadingHistory ? (
              <div style={styles.emptyState}>
                <Mail size={48} color="#ccc" style={{ marginBottom: 12 }} />
                <p>Нет отправленных писем</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Кому</th>
                      <th>Тема</th>
                      <th>Статус</th>
                      <th>Дата</th>
                      <th>Вложения</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.map(email => (
                      <tr
                        key={email.id}
                        className="history-row"
                        style={styles.tableRow}
                        onClick={() => setViewedEmail(email)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedEmailIds.includes(email.id)}
                            onChange={(e) =>
                              handleSelectOne(email.id, e.target.checked)
                            }
                          />
                        </td>
                        <td
                          style={{
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {email.to}
                        </td>
                        <td
                          style={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {email.subject}
                        </td>
                        <td>
                          {email.status === 'sent' ? (
                            <span style={{ color: '#28a745' }}>
                              <CheckCircle size={14} style={{ marginRight: 4 }} />
                              Отправлено
                            </span>
                          ) : (
                            <span style={{ color: '#ff4d4f' }}>
                              <AlertCircle size={14} style={{ marginRight: 4 }} />
                              Ошибка
                            </span>
                          )}
                        </td>
                        <td>{new Date(email.sentAt).toLocaleString()}</td>
                        <td>
                          {email.attachments?.length
                            ? email.attachments.map((att, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadAttachment(email.id, idx);
                                  }}
                                  style={styles.downloadBtn}
                                >
                                  <Download size={12} /> {att.filename.slice(0, 15)}
                                </button>
                              ))
                            : '—'}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleDeleteSingleEmail(email.id)}
                            className="trash-btn"
                            style={styles.trashBtn}
                            title="Удалить"
                          >
                            <Trash2 size={16} color="#ff4d4f" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {loadingHistory && (
                  <div style={{ textAlign: 'center', padding: 16, color: '#888' }}>
                    Загрузка...
                  </div>
                )}
                <div ref={observerRef} style={{ height: 10 }} />
              </div>
            )}
          </div>
        )}

        {/* ========== Вкладка ШАБЛОНЫ ========== */}
        {activeTab === 'templates' && (
          <div className="email-card" style={styles.card}>
            <button
              onClick={() => setShowTemplateForm(true)}
              style={styles.addButton}
            >
              <Plus size={16} /> Новый шаблон
            </button>
            {showTemplateForm && (
              <div style={styles.templateForm}>
                <input
                  type="text"
                  placeholder="Название шаблона"
                  value={newTemplate.name}
                  onChange={(e) =>
                    setNewTemplate({ ...newTemplate, name: e.target.value })
                  }
                  style={styles.input}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Тема письма"
                  value={newTemplate.subject}
                  onChange={(e) =>
                    setNewTemplate({ ...newTemplate, subject: e.target.value })
                  }
                  style={styles.input}
                  className="form-input"
                />
                <textarea
                  placeholder="HTML содержимое"
                  value={newTemplate.html}
                  onChange={(e) =>
                    setNewTemplate({ ...newTemplate, html: e.target.value })
                  }
                  rows={6}
                  style={styles.textarea}
                  className="form-input"
                />
                <div style={styles.buttonRow}>
                  <button onClick={handleSaveTemplate} style={styles.saveButton}>
                    Сохранить
                  </button>
                  <button
                    onClick={() => setShowTemplateForm(false)}
                    style={styles.cancelButton}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            )}
            {templates.length === 0 ? (
              <div style={styles.emptyState}>
                <FileText size={48} color="#ccc" style={{ marginBottom: 12 }} />
                <p>Нет шаблонов</p>
              </div>
            ) : (
              templates.map(t => (
                <div key={t.id} className="template-item" style={styles.templateItem}>
                  <div
                    style={{ flex: 1, cursor: 'pointer' }}
                    onClick={() =>
                      setShowFullTemplate(showFullTemplate === t.id ? null : t.id)
                    }
                  >
                    <strong>{t.name}</strong>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {t.subject}
                    </div>
                    {showFullTemplate === t.id && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: 12,
                          background: 'rgba(0,0,0,0.02)',
                          borderRadius: 8,
                          maxHeight: 200,
                          overflowY: 'auto',
                        }}
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: t.html }}
                          style={{ fontSize: 13, color: '#444' }}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => applyTemplate(t)}
                      className="action-btn"
                      style={styles.useButton}
                    >
                      Использовать
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(t.id, t.name)}
                      className="action-btn"
                      style={{ ...styles.trashBtn, color: '#ff4d4f' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Модалка просмотра письма */}
      {viewedEmail && (
        <div style={modalStyles.overlay} onClick={() => setViewedEmail(null)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <h3>Просмотр письма</h3>
              <button
                onClick={() => setViewedEmail(null)}
                style={modalStyles.closeButton}
              >
                <X size={20} />
              </button>
            </div>
            <div style={modalStyles.body}>
              <p><strong>Кому:</strong> {viewedEmail.to}</p>
              <p><strong>Тема:</strong> {viewedEmail.subject}</p>
              <p><strong>Дата:</strong> {new Date(viewedEmail.sentAt).toLocaleString()}</p>
              <div style={modalStyles.divider} />
              <div
                dangerouslySetInnerHTML={{ __html: viewedEmail.html }}
                style={modalStyles.emailContent}
              />
            </div>
            <div style={modalStyles.footer}>
              <button
                onClick={() => handleDeleteSingleEmail(viewedEmail.id)}
                style={modalStyles.deleteEmailButton}
              >
                <Trash2 size={16} /> Удалить
              </button>
              <button
                onClick={() => setViewedEmail(null)}
                style={modalStyles.closeModalButton}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </HRLayout>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px' },
  backButton: {
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '30px',
    padding: '8px 20px',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    marginBottom: 20,
    fontWeight: 500,
    fontSize: 14,
  },
  tabs: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  tab: {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 500,
    fontSize: 14,
    color: '#1a1a2e',
  },
  activeTab: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    boxShadow: '0 8px 20px rgba(37,117,252,0.3)',
  },
  card: {
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: 24,
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    border: '1px solid rgba(255,255,255,0.4)',
  },
  fieldGroup: { marginBottom: 16 },
  fieldLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: '#4a4a68',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    fontSize: 14,
    fontFamily: 'monospace',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  attachmentSection: { marginBottom: 16 },
  attachButton: {
    background: 'rgba(37,117,252,0.1)',
    border: '1px dashed #2575fc',
    color: '#2575fc',
    borderRadius: 30,
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 500,
  },
  attachmentsList: {
    marginTop: 12,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  attachmentItem: {
    background: 'rgba(0,0,0,0.04)',
    borderRadius: 20,
    padding: '4px 8px 4px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
  },
  removeAttachment: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
    padding: 0,
    display: 'flex',
  },
  buttonRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: {
    padding: '10px 16px',
    borderRadius: 30,
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.7)',
    minWidth: 180,
    fontSize: 14,
    outline: 'none',
  },
  sendButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    padding: '12px 28px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    fontSize: 14,
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
  },
  addButton: {
    background: 'rgba(37,117,252,0.1)',
    border: '1px solid #2575fc',
    color: '#2575fc',
    borderRadius: 30,
    padding: '8px 16px',
    cursor: 'pointer',
    marginBottom: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 500,
    fontSize: 14,
  },
  templateForm: {
    background: 'rgba(255,255,255,0.4)',
    padding: 20,
    borderRadius: 16,
    marginBottom: 20,
    border: '1px solid rgba(255,255,255,0.5)',
  },
  saveButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  cancelButton: {
    background: 'transparent',
    border: '1px solid #ccc',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    color: '#666',
  },
  templateItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.4)',
    borderRadius: 12,
    marginBottom: 8,
    border: '1px solid rgba(255,255,255,0.5)',
    gap: 12,
  },
  useButton: {
    background: 'rgba(37,117,252,0.1)',
    border: '1px solid #2575fc',
    color: '#2575fc',
    borderRadius: 20,
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
  },
  trashBtn: {
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 20,
    padding: '6px 10px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    color: '#999',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
    minWidth: 700,
  },
  tableWrapper: { overflowX: 'auto' },
  tableRow: {
    cursor: 'pointer',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 20,
    alignItems: 'center',
  },
  searchWrapper: {
    position: 'relative',
    flex: 2,
    minWidth: 200,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    borderRadius: 30,
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  dateFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  dateInput: {
    padding: '8px 12px',
    borderRadius: 20,
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.7)',
    fontSize: 13,
  },
  clearDatesBtn: {
    background: 'rgba(0,0,0,0.04)',
    border: '1px solid #ccc',
    borderRadius: 20,
    padding: '6px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
  },
  bulkActions: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    justifyContent: 'flex-end',
  },
  bulkButton: {
    background: 'rgba(37,117,252,0.08)',
    border: '1px solid #2575fc',
    borderRadius: 30,
    padding: '6px 16px',
    cursor: 'pointer',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#2575fc',
    fontWeight: 500,
  },
  downloadBtn: {
    background: 'rgba(37,117,252,0.08)',
    border: '1px solid #2575fc',
    borderRadius: 20,
    padding: '2px 8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: '#2575fc',
    marginRight: 4,
    whiteSpace: 'nowrap',
  },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16,
  },
  modal: {
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: 28,
    width: 650,
    maxWidth: '100%',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  closeButton: {
    background: 'rgba(255,255,255,0.5)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '50%',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#666',
  },
  body: { marginBottom: 20 },
  divider: {
    height: 1,
    background: 'rgba(0,0,0,0.08)',
    margin: '16px 0',
  },
  emailContent: {
    fontSize: 14,
    lineHeight: 1.6,
    color: '#1a1a2e',
    maxHeight: 400,
    overflowY: 'auto',
    padding: 16,
    background: 'rgba(0,0,0,0.02)',
    borderRadius: 12,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
  },
  closeModalButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  deleteEmailButton: {
    background: '#ff4d4f',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    padding: '10px 24px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 500,
  },
};

export default Email;